import { createUseStyles } from 'react-jss';
import color from 'color';

import { ThemeProps } from '@styles/theme';
import { flex } from '@resources/mixins';

export const useStyles = createUseStyles((theme: ThemeProps) => ({
    // Brings MUI inputs in line with the shared form inputs used by the other
    // modals (see @shared/components/forms/inputs/TextInput): black 16px value
    // text on white, 1.5px grey border, violet on hover/focus. The field label
    // itself is the shared <InputLabel>, rendered above the input.
    field: {
        width: '100%',
        '& .MuiOutlinedInput-root': {
            borderRadius: theme.radius,
            backgroundColor: theme.colors.white,
            fontFamily: theme.font.secondary,
            fontSize: 16,
            lineHeight: '24px',
            minHeight: 44,
            color: theme.colors.black,
            '& fieldset': {
                borderWidth: 1.5,
                borderColor: theme.colors.grey_light,
            },
            '&:hover fieldset': {
                borderColor: `${color(theme.colors.violet_primary).fade(0.62)}`,
            },
            '&.Mui-focused fieldset': {
                borderWidth: 1.5,
                borderColor: theme.colors.violet_primary,
            },
        },
        '& .MuiOutlinedInput-input': {
            color: theme.colors.black,
            padding: `${theme.spacing(2)}px ${theme.spacing(3)}px`,
        },
        // react-jss is injected after MUI (StyledEngineProvider injectFirst), so
        // the padding above also wins over the room MUI reserves for the select
        // arrow — give it back, and keep the value vertically centred.
        '& .MuiSelect-select': {
            ...flex('row', 'flex-start', 'center'),
            color: theme.colors.black,
            paddingRight: theme.spacing(8),
        },
        '& .MuiFormHelperText-root': {
            fontFamily: theme.font.secondary,
            color: theme.colors.grey_text,
            marginLeft: 0,
        },
    },
    fieldGroup: {
        ...flex('column', 'flex-start', 'stretch'),
        minWidth: 0,
    },
    modal: {
        width: theme.spacing(190),
        maxWidth: 'calc(100vw - 120px)',
        maxHeight: 'calc(100dvh - 80px)',
        fallbacks: [
            { maxHeight: 'calc(100vh - 80px)' },
        ],
        [theme.down('tablet')]: {
            maxWidth: 'calc(100vw - 16px)',
        },
    },
    header: {
        ...flex('column', 'flex-start', 'flex-start'),
        gap: theme.spacing(1),
        marginBottom: theme.spacing(3),
    },
    tabs: {
        minHeight: 0,
        marginBottom: theme.spacing(3),
        borderBottom: `1px solid ${theme.colors.grey_light}`,
    },
    tabDescription: {
        marginBottom: theme.spacing(3),
    },
    deploymentRow: {
        ...flex('row', 'center', 'center'),
        gap: theme.spacing(3),
        marginBottom: theme.spacing(3),
        flexWrap: 'wrap',
    },
    deploymentPicker: {
        width: theme.spacing(70),
        maxWidth: '100%',
    },
    settings: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: theme.spacing(3),
        alignItems: 'start',
        padding: theme.spacing(3),
        marginBottom: theme.spacing(3),
        borderRadius: theme.radius / 2,
        backgroundColor: theme.colors.transp_grey_light,
        [theme.down('tablet')]: {
            gridTemplateColumns: 'minmax(0, 1fr)',
        },
    },
    checkboxLabel: {
        fontFamily: theme.font.secondary,
        fontSize: 16,
        lineHeight: 1.25,
        color: theme.colors.black,
    },
    settingsToggle: {
        ...flex('row', 'flex-start', 'center'),
        cursor: 'pointer',
        fontFamily: theme.font.secondary,
        fontSize: 16,
        fontWeight: 600,
        color: theme.colors.violet_primary,
        background: 'none',
        border: 'none',
        padding: 0,
    },
    toolbar: {
        ...flex('row', 'center', 'center'),
        gap: theme.spacing(3),
        marginBottom: theme.spacing(3),
        flexWrap: 'wrap',
    },
    list: {
        maxHeight: '45dvh',
        fallbacks: [
            { maxHeight: '45vh' },
        ],
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        WebkitOverflowScrolling: 'touch',
        border: `1.5px solid ${theme.colors.grey_light}`,
        borderRadius: theme.radius / 2,
    },
    row: {
        ...flex('row', 'flex-start', 'center'),
        gap: theme.spacing(2),
        padding: `${theme.spacing(2)}px ${theme.spacing(3)}px`,
        borderBottom: `1px solid ${theme.colors.grey_light}`,
        '&:last-child': {
            borderBottom: 'none',
        },
    },
    rowSelected: {
        backgroundColor: theme.colors.transp_violet,
    },
    procedureImage: {
        width: 56,
        height: 40,
        objectFit: 'cover',
        borderRadius: theme.radius / 2,
        backgroundColor: theme.colors.grey_light,
        flexShrink: 0,
    },
    procedureInfo: {
        ...flex('column', 'center', 'flex-start'),
        minWidth: 0,
        flexGrow: 1,
    },
    // MUI's palette.text.primary is grey_text app-wide, so the row title is
    // spelled out here to match the black text of the shared form inputs.
    procedureName: {
        fontFamily: theme.font.secondary,
        fontSize: 16,
        lineHeight: 1.25,
        fontWeight: 600,
        color: theme.colors.black,
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    procedureType: {
        fontFamily: theme.font.secondary,
        fontSize: 13,
        lineHeight: 1.3,
        color: theme.colors.grey_text,
    },
    searchField: {
        width: theme.spacing(60),
        maxWidth: '100%',
    },
    versionPicker: {
        width: theme.spacing(45),
        flexShrink: 0,
        [theme.down('tablet')]: {
            width: theme.spacing(32),
        },
    },
    placeholder: {
        ...flex('row', 'center', 'center'),
        minHeight: theme.spacing(30),
        padding: theme.spacing(4),
        textAlign: 'center',
    },
    footer: {
        ...flex('row', 'flex-end', 'center'),
        gap: theme.spacing(3),
        marginTop: theme.spacing(4),
    },
    steps: {
        marginTop: theme.spacing(4),
        padding: theme.spacing(3),
        borderRadius: theme.radius / 2,
        backgroundColor: theme.colors.transp_grey_light,
    },
    stepsTitle: {
        fontFamily: theme.font.secondary,
        fontSize: 16,
        fontWeight: 600,
        color: theme.colors.black,
    },
    stepsList: {
        margin: `${theme.spacing(2)}px 0 0`,
        paddingLeft: theme.spacing(5),
        // reset.scss strips list markers app-wide.
        listStyle: 'decimal',
        fontFamily: theme.font.secondary,
        fontSize: 14,
        lineHeight: 1.5,
        color: theme.colors.grey_text,
        '& li + li': {
            marginTop: theme.spacing(1),
        },
    },
}));
